// Created: 2026-09-03
// Updated: 2026-09-03
//
// The Listing_Bucket vocabulary, as the CLIENT needs it (content-visibility-model, Requirements 5.3,
// 5.4, 12.1, task 29).
//
// ## What this is, and what it deliberately is not
//
// The backend authority for a listing bucket is
// `packages/lambda-utilities/src/utils/state-helpers.ts`: it owns the derivation
// (`shelf_state === 'archived' ? 'archived' : visibility`, written as a total 2x3 table), the
// `GSI3SK` prefixes, and the key parser. **None of that belongs here and none of it is copied here.**
// The website never builds a key, never reads a prefix and never derives a bucket from two axes — it
// only needs the four TOKENS that go on the wire as `?bucket=`, because task 18 gave four owner
// listing routes that parameter and task 29 is the control that drives it.
//
// The website cannot import the authority: `@mrb91002/lambda-utilities` is not a dependency of this
// package (and its index pulls in the AWS SDK). So the token set is DERIVED here from the same two
// axis value sets the authority derives from — `Visibility` and `SHELF_STATE`, both from
// `@mrb91002/shared-types`, which this package does depend on. Adding an Access_Level therefore grows
// this list without an edit, exactly as it grows `CONTENT_LISTING_BUCKETS`, and the four tokens cannot
// drift from the enum they come from.
//
// Spelling the four out as string literals was the alternative and is what this file exists to avoid.
// That would be a second opinion about a value that has an authority — the same mistake, one layer up,
// that BL-211 records forty times over.
//
// ## Why `archived` sits beside three Access_Levels here and nowhere else
//
// A bucket is not an axis. It is the single derived value a listing query range-scans on, so its
// vocabulary is "the three Access_Levels, plus `archived` when the shelf wins". That is why this file
// is the ONLY place in the website where an Access_Level token and a Shelf_State token appear in one
// union — everywhere else they must stay apart, which is the whole point of the two-axis split
// (Requirement 3, terminology discipline).

import { SHELF_STATE, Visibility } from '@mrb91002/shared-types'

/**
 * A listing bucket token as it travels on the wire: `public | unlisted | private | archived`.
 *
 * Structurally identical to `ListingBucket` in the backend authority, and deliberately expressed as
 * `Visibility | 'archived'` rather than as a fresh union of four literals, so the three Access_Level
 * members are the enum's members and not a restatement of them.
 */
export type ListingBucket = Visibility | typeof SHELF_STATE.ARCHIVED

/**
 * The four buckets by name, so a call site can branch on one without reaching for `Visibility` or
 * `SHELF_STATE` itself — which is what would put an Access_Level token and a Shelf_State token side by
 * side in a component, the mixing this module exists to contain.
 *
 * Named to mirror `LISTING_BUCKET` in the backend authority exactly, so the two read as one vocabulary
 * rather than two. Every member is a REFERENCE to the axis value, never a re-spelling of it.
 *
 * The bucket-prefix guard knows this name: `scripts/check-bucket-prefixes.mjs` CHECK 3 treats
 * `LISTING_BUCKET.PUBLIC` and its siblings as bucket tokens, so a two-way ternary between any pair of
 * them fails the build here exactly as it does on the backend.
 */
export const LISTING_BUCKET = {
  /** In every discovery surface, on the profile, and the only bucket a visitor is served. */
  PUBLIC: Visibility.PUBLIC,
  /** Out of discovery; opens by link. */
  UNLISTED: Visibility.UNLISTED,
  /** Out of discovery; refused to a third party. */
  PRIVATE: Visibility.PRIVATE,
  /** Shelved by the owner, at whatever Access_Level it kept. */
  ARCHIVED: SHELF_STATE.ARCHIVED,
} as const

/**
 * Every bucket, in exposure order: public, unlisted, private, archived.
 *
 * Derived from {@link LISTING_BUCKET} rather than listed again, and that object is itself derived from
 * the two axis value sets — so the order here is the `Visibility` declaration order followed by the
 * archived Shelf_State, the same order `LISTING_BUCKETS` produces in the backend authority, and the
 * order the owner's segments read in.
 */
export const LISTING_BUCKETS: readonly ListingBucket[] = Object.values(LISTING_BUCKET)

/**
 * The bucket a listing shows when nobody has chosen one.
 *
 * `public` — the first segment, the bucket a visitor sees, and the documented default of every
 * `?bucket=` route (`get-secure-profile.ts`, `get-user-craft-projects.ts`, `get-craft-room.ts`,
 * `my-collections.ts`). The client sends the parameter EXPLICITLY anyway rather than relying on that
 * default, so which listing a request asked for is visible in the URL rather than inferred from its
 * absence.
 */
export const DEFAULT_LISTING_BUCKET: ListingBucket = LISTING_BUCKET.PUBLIC

/**
 * The user-facing label for each bucket. A total `Record`, so a new bucket is a compile error here
 * rather than an unlabelled segment.
 *
 * The words match the enum tokens exactly, which is Requirement 3.1: the label, the API field, the
 * enum value and the key prefix are all the same word, so nobody has to translate between layers.
 */
export const LISTING_BUCKET_LABEL: Record<ListingBucket, string> = {
  [LISTING_BUCKET.PUBLIC]: 'Public',
  [LISTING_BUCKET.UNLISTED]: 'Unlisted',
  [LISTING_BUCKET.PRIVATE]: 'Private',
  [LISTING_BUCKET.ARCHIVED]: 'Archived',
}

/**
 * One sentence per bucket, stating plainly whether someone holding the link can open the item.
 *
 * Used as the segment's accessible description. The copy rule is Requirement 11.5's and the wording
 * is deliberately the same wording task 30's settings surface will use, so the two surfaces cannot
 * describe the same level differently — which is the drift Requirement 11.4 exists to prevent.
 */
export const LISTING_BUCKET_DESCRIPTION: Record<ListingBucket, string> = {
  [LISTING_BUCKET.PUBLIC]: 'In search and on your profile. Anyone can open it.',
  [LISTING_BUCKET.UNLISTED]: 'Not in search and not on your profile. Anyone holding the link can open it.',
  [LISTING_BUCKET.PRIVATE]: 'Not in search and not on your profile. Only you can open it.',
  [LISTING_BUCKET.ARCHIVED]: 'Put away. Existing links keep working.',
}

/**
 * What happens when somebody who ALREADY HAS THE LINK opens it. One sentence per bucket, and the
 * sentence names the link every time (task 30, Requirement 11.5).
 *
 * Separate from {@link LISTING_BUCKET_DESCRIPTION} rather than folded into it, because the two answer
 * different questions and only one of them is the question this spec exists to settle. The description
 * answers "where does this appear"; this answers "can a person holding the link open it" — and the reason
 * that needs its own line is that `public` and `unlisted` have the SAME answer here and differ only on
 * the description. A reader who is shown only the description can conclude that unlisted means hidden,
 * which is precisely the false belief the old Make Private dialog encoded.
 *
 * `public` and `unlisted` therefore carry IDENTICAL copy, deliberately. That is the model
 * (Requirement 1.2, 1.3), not an oversight, and it is what makes `unlisted` different from `private`.
 *
 * The `archived` entry restates Requirement 11.7 in the same words the description uses, because "archived"
 * does not by itself imply that existing links keep working, and the Archive action is where an owner is
 * most likely to assume it means "taken down".
 */
export const LISTING_BUCKET_LINK_OUTCOME: Record<ListingBucket, string> = {
  [LISTING_BUCKET.PUBLIC]: 'Anyone holding the link can open it.',
  [LISTING_BUCKET.UNLISTED]: 'Anyone holding the link can open it.',
  [LISTING_BUCKET.PRIVATE]: 'Someone holding the link cannot open it. Only you can.',
  [LISTING_BUCKET.ARCHIVED]: 'Existing links keep working.',
}

/**
 * Interpret a bucket token that came from outside — a URL, a stored preference, a test fixture —
 * returning `undefined` when it names no bucket.
 *
 * Mirrors `listingBucketFromToken` in the backend authority, including its refusal to substitute a
 * default: a caller that silently fell back to `public` on a typo would render the wrong listing with
 * no way for anyone to notice. The caller decides.
 */
export function listingBucketFromToken(token: string | null | undefined): ListingBucket | undefined {
  if (!token) return undefined
  return LISTING_BUCKETS.find((bucket) => bucket === token)
}

/*
 * `bucketHasContinuation` used to live here and is GONE (content-visibility-model task 49; BL-330).
 *
 * It answered "can this bucket's Load More work?" with `bucket === public`, and it was the flag that
 * encoded task 29's deliberate suppression: the only continuation endpoint for an author's patterns was
 * the anonymous `GET /api/author/{screenName}/patterns`, whose key condition is `public#`, so replaying a
 * non-public cursor against it would have appended PUBLIC patterns underneath the Unlisted heading — the
 * wrong rows, silently, in the one place an owner looks to find out what is unlisted. Withholding the
 * cursor and the button was the lesser fault, and it named the missing route.
 *
 * `GET /api/secure/patterns?bucket=&lastKey=` is that route, so every bucket now has a continuation and
 * there is nothing left for this predicate to distinguish. It is REMOVED rather than left returning
 * `true` for everything: a suppression flag that suppresses nothing is a thing the next reader has to
 * work out is inert.
 *
 * The defect it protected against did not go away — a cursor from one bucket must not continue another —
 * it moved to where it can be prevented instead of avoided. The route REFUSES with 400 a cursor whose
 * position names a different bucket or a different owner's partition
 * (`readPatternListingPosition` in `services/lambda-secure/src/routes/patterns/list-owner-patterns.ts`).
 */
